import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { ErrorBoundary } from '@everyday-os/ui';
import { logger } from '@everyday-os/logger';

// --- Global Initialization ---
import './index.css'; // Tailwind and global aesthetic resets
import './i18n';      // i18next localization engine

/**
 * 🚀 PERFORMANCE & ERROR TELEMETRY
 * Conditional import to prevent build errors (TS2307) if LogRocket
 * is not present in the local environment.
 */
const initTelemetry = async () => {
  if (import.meta.env.PROD) {
    try {
      const LogRocket = (await import('logrocket')).default;
      const setupLogRocketReact = (await import('logrocket-react')).default;

      const lrAppId = (import.meta as any).env?.VITE_LOGROCKET_APP_ID;
      if (lrAppId) {
        LogRocket.init(lrAppId);
        setupLogRocketReact(LogRocket);
        logger.setErrorReporter((msg, err) =>
          LogRocket.captureException(err instanceof Error ? err : new Error(msg))
        );
        logger.log('Telemetry uplink active');
      }
    } catch (e) {
      logger.warn('Telemetry failed to initialize. Continuing without error reporting.');
    }
  }
};

initTelemetry();

/**
 * 🌳 THE ROOT MOUNT
 * We wrap the App in BrowserRouter here so that the entire
 * OS can leverage the navigation engine and URL-based routing.
 */
const rootElement = document.getElementById('root');

if (!rootElement) {
  throw new Error("Target container 'root' not found. Ensure index.html is correctly configured.");
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <ErrorBoundary onError={(err, info) => logger.error('Root render error', { err, info })}>
      <BrowserRouter>
        {/* Note: AuthProvider and ThemeProvider are wrapped inside
          the App component to handle internal context dependencies.
        */}
        <App />
      </BrowserRouter>
    </ErrorBoundary>
  </React.StrictMode>
);
